import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { AuthManager as SharedAuthManager, AuthManagerConfig } from '@privemind/auth';
import { usePortfolioAuth } from '../../../lib/use-portfolio-auth';
import { portfolioAuthIntegration, PortfolioUserData } from '../../../lib/auth-integration';
import { portfolioStore } from '../../../stores/portfolio-store';

interface CryptoAuthManagerProps {
  children: React.ReactNode;
}

export const AuthManager: React.FC<CryptoAuthManagerProps> = observer(({ children }) => {
  const auth = usePortfolioAuth();

  useEffect(() => {
    // Set up the integration with portfolio store
    portfolioAuthIntegration.setPortfolioStore(portfolioStore);
  }, []);

  // Custom migration summary renderer for portfolio data
  const renderMigrationSummary = (data: PortfolioUserData) => {
    const bucketCount = Object.keys(data.buckets || {}).length;
    const assetCount = Object.keys(data.assets || {}).length;
    
    return (
      <ul>
        <li>{bucketCount} bucket(s)</li>
        <li>{assetCount} asset(s)</li>
      </ul>
    );
  };

  // Create auth manager config
  const config: AuthManagerConfig<PortfolioUserData> = {
    authStore: auth.authStore,
    appName: 'Crypto Portfolio',
    passwordPrompt: null, // Will be managed by the auth system
    migrationPrompt: auth.migration.shouldPromptMigration() ? {
      localStorageData: auth.migration.migrationData?.userData || { buckets: {}, assets: {} },
      onMigrate: async () => {
        await auth.migration.migratePortfolioData();
      },
      onSkip: async () => {
        auth.migration.skipMigration();
      },
      onRetry: async () => {
        await auth.migration.migratePortfolioData();
      }
    } : null,
    isMigrationInProgress: auth.migration.isProcessing || auth.migration.isMigrating,
    migrationError: auth.migration.processingError || auth.migration.migrationError,
    renderMigrationSummary,
    loadingMessage: 'Initializing Secure Storage...',
    loadingDescription: 'Setting up encrypted portfolio storage'
  };

  return (
    <SharedAuthManager config={config}>
      {children}
    </SharedAuthManager>
  );
});
